import { SubmitCartPayload } from 'apps/commerce/cart/types';
import axios from 'axios';
import applyCaseMiddleware from 'axios-case-converter';
import { PATHS } from '../constants';
import buildOrderPayload from './buildOrderPayload';

export class IncompletePaymentMethodError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'IncompletePaymentMethodError';
  }
}

export class NoPaymentMethodError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'NoPaymentMethodError';
  }
}

export class NoPaymentIntegrationError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'NoPaymentIntegrationError';
  }
}

const instance = applyCaseMiddleware(
  axios.create({
    xsrfCookieName: 'CART-CSRF-TOKEN',
    xsrfHeaderName: 'X-CSRF-Token',
    withCredentials: true,
    headers: {
      'Accept': 'application/json',
      'Content-Type': 'application/json',
    },
  }),
);

export const getRequest = (url: string, params: any = undefined) => instance.get(url, { params });
export const postRequest = (url: string, data: any) => instance.post(url, data);
export const patchRequest = (url: string, data: any) => instance.patch(url, data);

export const fetchCart = async () => {
  const response = await getRequest(PATHS.API.CART);
  return response.data;
};

export const addCartItem = async (itemToken: string) => {
  const params = { quantity: 1, item: { type: 'offer', token: itemToken } };
  const response = await postRequest(PATHS.API.ADD_ITEM, params);
  return response.data;
};

export const updateCartItem = async (cartItemId: string, quantity: number) => {
  const params = { cartItemId, quantity };
  const response = await patchRequest(PATHS.API.UPDATE_ITEM, params);
  return response.data;
};

export const createPaymentMethod = async (
  elements: any,
  email: any,
  stripe: {
    createPaymentMethod: (arg0: {
      elements: any;
      // eslint-disable-next-line camelcase
      params: { billing_details: { email: any } };
    }) => PromiseLike<{ error: any; paymentMethod: any }> | { error: any; paymentMethod: any };
  },
) => {
  const { error, paymentMethod } = await stripe.createPaymentMethod({
    elements,
    params: {
      billing_details: { email },
    },
  });

  if (error) {
    if (error.code === 'incomplete') {
      // we prevent the user from submitting incomplete card details, however,
      // in the case of apple pay, you can wind up in this state if the user
      // dismisses the apple pay modal without completing the payment...
      throw new IncompletePaymentMethodError('Incomplete payment method');
    }
    throw new NoPaymentMethodError('Payment method is required but was not created');
  }

  return paymentMethod;
};

export const submitCart = async (getValues: Function) => {
  const params: SubmitCartPayload = await buildOrderPayload(getValues);
  const response = await postRequest(PATHS.API.ORDER_CHECKOUT, params);
  return response.data;
};

export const fetchOrderCheckoutStatus = async (id: string) => {
  const response = await getRequest(`${PATHS.API.ORDER_CHECKOUT}/${id}/status`);
  return response.data;
};

export const fetchCheckoutSettings = async () => {
  const response = await getRequest(`${PATHS.API.CART}/checkout_settings`);
  return response.data;
};

export const calculateTax = async (addressCountry: string, addressZip: string, taxId: string) => {
  const params = {
    address_country: addressCountry,
    address_zip: addressZip,
    tax_id: taxId,
  };
  const response = await postRequest(`${PATHS.API.CALCULATE_TAX}`, params);
  return response.data;
};

export const fetchTaxIdStatus = async (country: string, taxId: string | undefined) => {
  const params = { country, taxId };
  const response = await getRequest(`${PATHS.API.TAX_ID_STATUS}`, params);
  return response.data;
};

export const clearCoupon = async () => {
  const params = {};
  const response = await postRequest(`${PATHS.API.CLEAR_COUPON}`, params);
  return response.data;
};

export const validateCoupon = async (couponCode: string) => {
  const params = { couponCode };
  const response = await postRequest(`${PATHS.API.VALIDATE_COUPON}`, params);
  return response.data;
};

export const createPaypalOrder = async (options: any) => {
  const response = await postRequest(`${PATHS.API.PAYPAL_PAYMENTS}`, options);
  return response.data;
};

const cartClient = {
  getRequest,
  postRequest,
  fetchCart,
  addCartItem,
  updateCartItem,
  submitCart,
  fetchOrderCheckoutStatus,
  fetchCheckoutSettings,
  calculateTax,
  fetchTaxIdStatus,
  clearCoupon,
  validateCoupon,
  createPaypalOrder,
};

export default cartClient;
